// src/components/RehearsalAnnouncer.jsx
//
// Watches scheduledBeatsRef and speaks rehearsal marks (or count-in numbers)
// on the downbeat of marked measures during playback. Mirrors the rAF/lookahead
// pattern from BeatHighlighter.
import { useEffect, useRef } from "react";
import { speak, cancelSpeech, pingSpeechKeepalive, warmUpSpeech } from "../utils/speech";

// Skip announcements that are within this window of the previous one. Guards
// against the scheduler pushing duplicate downbeats (~60ms apart in practice)
// without affecting legitimate adjacent measures, which are at least several
// hundred ms apart at any realistic tempo.
const DEDUP_WINDOW_SEC = 0.25;

function RehearsalAnnouncer({
  audioCtx,
  scheduledBeatsRef,
  isPlaying,
  rehearsalLetters,
  countInMeasures,
  measuresLength,
  leadTimeMs = 150,
}) {
  const rafRef = useRef(null);
  const lastSpokenStartTimeRef = useRef(-Infinity);

  // Mirror the latest values into refs so we don't tear down the rAF loop
  // every time the user edits a rehearsal letter or flips a setting.
  const lettersRef = useRef(rehearsalLetters);
  const countInRef = useRef(countInMeasures);
  const measuresLenRef = useRef(measuresLength);
  useEffect(() => { lettersRef.current = rehearsalLetters; }, [rehearsalLetters]);
  useEffect(() => { countInRef.current = countInMeasures; }, [countInMeasures]);
  useEffect(() => { measuresLenRef.current = measuresLength; }, [measuresLength]);

  useEffect(() => {
    if (!isPlaying || !audioCtx) {
      cancelSpeech();
      lastSpokenStartTimeRef.current = -Infinity;
      return;
    }

    // Spin up the synth immediately so the first real announcement doesn't
    // pay cold-start latency. Mount happens on play (a user gesture), which
    // is the right context for this on browsers that gate speech.
    warmUpSpeech();

    // Keep Chrome's speechSynthesis warm — without this, the synth can wedge
    // into a paused state across stop/restart cycles and silently drop the
    // first several utterances of the next session.
    const keepaliveId = setInterval(pingSpeechKeepalive, 5000);
    pingSpeechKeepalive();

    function buildAnnouncement(beat) {
      const len = measuresLenRef.current || 1;
      const letters = lettersRef.current || {};
      const countIn = countInRef.current || 0;
      const m = beat.measureIndex;
      // The scheduler emits placeholder beats with measureIndex < 0 during its
      // own count-in clicks — ignore those.
      if (m < 0) return null;
      const localIdx = ((m % len) + len) % len;
      const own = letters[localIdx];
      if (own) return String(own);
      if (countIn > 0) {
        for (let k = 1; k <= countIn; k++) {
          const target = m + k;
          // Clamp at piece start: only count in to marks within the same loop
          // pass, never wrap. This also handles the start of the piece — at
          // measure 0 with a mark at measure 2, we say "2" then "1".
          if (target >= len) break;
          const t = ((target % len) + len) % len;
          if (letters[t]) return String(k);
        }
      }
      return null;
    }

    function loop() {
      const arr = scheduledBeatsRef.current;
      if (!Array.isArray(arr) || arr.length === 0) {
        rafRef.current = requestAnimationFrame(loop);
        return;
      }
      const nowSec = audioCtx.currentTime;
      const lookAhead = leadTimeMs / 1000;

      // The array is sorted by startTimeSec, so we can break as soon as we hit
      // a beat whose trigger time is still in the future.
      for (let i = 0; i < arr.length; i++) {
        const beat = arr[i];
        if (beat.beatIndex !== 0) continue;
        if (beat.startTimeSec - lookAhead > nowSec) break;

        // Two-stage filter. First: skip beats already processed (same start
        // or earlier). The array keeps beats around for a while after their
        // startTime passes, so without this we'd re-iterate history every
        // frame. Second: skip near-duplicates that the App.js scheduler
        // occasionally emits — two downbeats for the same measure with
        // slightly different startTimes (~60ms apart, typically around the
        // count-in / first-real measure boundary). Without that guard each
        // duplicate fires a fresh speak() that cancels the previous one
        // mid-utterance. The window is well under one measure at any
        // realistic tempo, so legitimate consecutive downbeats are fine.
        const sinceLastSec = beat.startTimeSec - lastSpokenStartTimeRef.current;
        if (sinceLastSec <= 0) continue;
        if (sinceLastSec < DEDUP_WINDOW_SEC) continue;

        const text = buildAnnouncement(beat);
        if (text) speak(text);
        lastSpokenStartTimeRef.current = beat.startTimeSec;
        // One announcement per frame. The next eligible beat will fire on a
        // later frame; since real downbeats are at minimum hundreds of ms
        // apart, that's effectively no delay.
        break;
      }

      rafRef.current = requestAnimationFrame(loop);
    }

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      clearInterval(keepaliveId);
      cancelSpeech();
    };
  }, [audioCtx, scheduledBeatsRef, isPlaying, leadTimeMs]);

  return null;
}

export default RehearsalAnnouncer;
